import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { ShieldOff, LogIn, Headphones, Zap } from 'lucide-react';

const PlanExpired = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const appName = process.env.REACT_APP_APP_NAME || 'CRM Automax';
  const isSuperAdmin = user?.role === 'super-admin';

  return (
    <div className="auth-page-wrapper" style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'linear-gradient(135deg, #0a0f1e 0%, #0f172a 50%, #0a0f1e 100%)',
      fontFamily: "'Inter', sans-serif",
      padding: '24px',
    }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap');
        @keyframes fadeUp { from { opacity:0; transform:translateY(24px); } to { opacity:1; transform:translateY(0); } }
        @keyframes pulse-ring {
          0%   { transform: scale(1);   opacity: 0.6; }
          100% { transform: scale(1.5); opacity: 0; }
        }
      `}</style>

      <div className="auth-card-container" style={{
        width: '100%', maxWidth: '460px',
        background: 'rgba(255,255,255,0.03)',
        border: '1px solid rgba(255,255,255,0.08)',
        borderRadius: '28px',
        padding: '56px 44px',
        textAlign: 'center',
        backdropFilter: 'blur(20px)',
        boxShadow: '0 30px 80px rgba(0,0,0,0.5)',
        animation: 'fadeUp 0.6s ease',
      }}>

        {/* Icon with pulse ring */}
        <div style={{ position: 'relative', display: 'inline-flex', marginBottom: '32px' }}>
          <div style={{
            position: 'absolute', inset: '-8px', borderRadius: '50%',
            border: '1px solid rgba(239,68,68,0.4)',
            animation: 'pulse-ring 2s ease-out infinite',
          }} />
          <div style={{
            width: '80px', height: '80px', borderRadius: '50%',
            background: 'linear-gradient(135deg, rgba(239,68,68,0.2), rgba(220,38,38,0.1))',
            border: '1px solid rgba(239,68,68,0.3)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <ShieldOff size={38} color="#f87171" />
          </div>
        </div>

        <h1 style={{
          fontSize: '26px', fontWeight: '800', color: '#f8fafc',
          letterSpacing: '-0.02em', marginBottom: '12px',
        }}>
          Plan Expired
        </h1>
        <p style={{
          fontSize: '15px', color: '#94a3b8', lineHeight: 1.7, marginBottom: '8px',
        }}>
          Your company's {appName} subscription has expired.
        </p>
        <p style={{
          fontSize: '14px', color: '#64748b', lineHeight: 1.7, marginBottom: '40px',
        }}>
          {isSuperAdmin
            ? 'Your subscription has expired. Upgrade or renew your plan to restore access for your entire team.'
            : <>You cannot log in at this time. Please contact your
              <strong style={{ color: '#94a3b8' }}> Super Admin</strong> to renew the plan
              and restore your access.</>
          }
        </p>

        {/* Status pill */}
        <div style={{
          display: 'inline-flex', alignItems: 'center', gap: '8px',
          background: 'rgba(239,68,68,0.08)',
          border: '1px solid rgba(239,68,68,0.2)',
          padding: '10px 20px', borderRadius: '12px',
          marginBottom: '36px',
        }}>
          <div style={{
            width: '8px', height: '8px', borderRadius: '50%',
            background: '#f87171',
          }} />
          <span style={{ fontSize: '13px', fontWeight: '600', color: '#f87171' }}>
            Subscription Inactive
          </span>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {isSuperAdmin ? (
            <button
              onClick={() => navigate('/admin/pricing')}
              style={{
                width: '100%', padding: '14px',
                background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                color: 'white', border: 'none', borderRadius: '12px',
                fontSize: '15px', fontWeight: '700', cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                transition: 'all 0.2s ease',
                boxShadow: '0 8px 20px -4px rgba(37, 99, 235, 0.4)',
              }}
              onMouseOver={e => e.currentTarget.style.transform = 'translateY(-2px)'}
              onMouseOut={e => e.currentTarget.style.transform = 'translateY(0)'}
            >
              <Zap size={17} /> Upgrade Subscription
            </button>
          ) : (
            <button
              onClick={() => navigate('/login')}
              style={{
                width: '100%', padding: '14px',
                background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                color: 'white', border: 'none', borderRadius: '12px',
                fontSize: '15px', fontWeight: '700', cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                transition: 'all 0.2s ease',
                boxShadow: '0 8px 20px -4px rgba(37, 99, 235, 0.4)',
              }}
              onMouseOver={e => e.currentTarget.style.transform = 'translateY(-2px)'}
              onMouseOut={e => e.currentTarget.style.transform = 'translateY(0)'}
            >
              <LogIn size={17} /> Back to Login
            </button>
          )}

          <a
            href="mailto:support@crmAutomax.com"
            style={{
              width: '100%', padding: '14px',
              background: 'transparent',
              color: '#94a3b8',
              border: '1px solid rgba(255,255,255,0.08)',
              borderRadius: '12px', fontSize: '15px', fontWeight: '600',
              cursor: 'pointer', textDecoration: 'none',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
              transition: 'all 0.2s ease',
              boxSizing: 'border-box',
            }}
            onMouseOver={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.2)'; e.currentTarget.style.color = '#f8fafc'; }}
            onMouseOut={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.08)'; e.currentTarget.style.color = '#94a3b8'; }}
          >
            <Headphones size={17} /> Contact Support
          </a>
        </div>

        <p style={{ marginTop: '28px', fontSize: '12px', color: '#334155' }}>
          {appName} &nbsp;·&nbsp; Subscription Management
        </p>
      </div>
    </div>
  );
};

export default PlanExpired;
